import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { createCustomerPortalStripeSession, getCustomerPortalBilling } from '../../api/customerPortalApi';
import { formatBillingInterval } from '../../utils/customerPortalLabels';
import { stripeCheckoutReturnState } from '../../utils/stripeCheckoutReturn';
import { getPortalCustomerPricingSummary } from '../../api/pricingApi';
import { getPortalRolePermissionCleanupSummary } from '../../api/permissionCleanupApi';
import {
  createPortalStripeCheckoutSession,
  downloadPortalCustomerInvoice,
  downloadPortalCustomerInvoicePdf,
  getPortalCustomerBillingOverview,
  getPortalCustomerBillingProfile,
  getPortalCustomerInvoices,
  getPortalPaidPeriods,
  getPortalPaymentMethods,
  getPortalSavedCard,
  createPortalSavedCardSetup,
  revokePortalSavedCard,
  detachPortalSavedCard,
  updatePortalCustomerBillingProfile,
} from '../../api/billingPortalApi';
import {
  cancelPortalSubscriptionChange,
  cancelPortalSubscriptionRenewal,
  getPortalSubscriptionChangeRequests,
  getPortalTrialConversion,
  revokePortalTrialConversionConsent,
  storePortalTrialConversionConsent,
} from '../../api/subscriptionChangesApi';
import {
  canDownloadInvoicePdf,
  getInvoiceDisplayNumber,
  isExternalAccountingInvoice,
} from '../../billing/externalInvoiceDisplay';
import { stripeOnlinePaymentControlsFromPaymentMethods } from '../../billing/stripeOnlinePaymentUi';
import { colorTokens, typographyTokens } from '../../app/designTokens';
import { SubscriptionChangeDialog } from '../../components/SubscriptionChangeDialog';
import { SavedCardSection } from '../../components/SavedCardSection';
import { TrialConversionConsentPanel } from '../../components/TrialConversionConsentPanel';
import { DataTable } from '../../components/DataTable';
import { DetailField } from '../../components/InfoCard';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { MetricCard } from '../../components/MetricCard';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { TableToolbar } from '../../components/TableToolbar';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';

const { semantic } = colorTokens;

const metricGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: '1fr 1fr',
    md: 'repeat(3, 1fr)',
    lg: 'repeat(5, 1fr)',
  },
  gap: 2,
  mb: 2.5,
};

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2.5,
};

const numberSx = {
  fontFamily: typographyTokens.fontFamilyMono,
  fontSize: 13,
  lineHeight: '20px',
  fontWeight: typographyTokens.fontWeightSemibold,
  color: semantic.text.primary,
  whiteSpace: 'nowrap',
};

const mutedHelperSx = {
  mt: 0.5,
  fontSize: 12,
  lineHeight: '16px',
  color: semantic.text.muted,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
};

const tableSx = {
  overflowX: 'auto',
  width: '100%',
  '& table': {
    width: '100%',
    minWidth: '100%',
  },
};

function CountBadge({ label }) {
  return (
    <StatusBadge
      status="list"
      category="generic"
      apiLabel={label}
      size="Sm"
      tone="Neutral"
    />
  );
}

export function CustomerPortalBillingPage() {
  const { t } = useTranslation([
    'billing',
    'common',
    'pricing',
    'subscriptionChanges',
    'permissionCleanup',
    'stripe',
    'customers',
  ]);
  const { activeCustomerId, loading: portalLoading } = useCustomerPortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState({ subscriptions: [], payments: [] });
  const [overview, setOverview] = useState(null);
  const [pricingSummary, setPricingSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [profileForm, setProfileForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [portalMessage, setPortalMessage] = useState('');
  const [portalMessageSeverity, setPortalMessageSeverity] = useState('info');
  const [paidPeriods, setPaidPeriods] = useState([]);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileMessageSeverity, setProfileMessageSeverity] = useState('info');
  const [changeRequests, setChangeRequests] = useState([]);
  const [cleanupSummary, setCleanupSummary] = useState(null);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [cancelingRenewal, setCancelingRenewal] = useState(false);
  const [trialConversion, setTrialConversion] = useState(null);
  const [trialConsentAccepted, setTrialConsentAccepted] = useState(false);
  const [savingTrialConsent, setSavingTrialConsent] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [savedCardConsent, setSavedCardConsent] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const emptyValue = t('common:empty_value');
  const stripeUi = stripeOnlinePaymentControlsFromPaymentMethods(paymentMethods);

  useEffect(() => {
    const stripeReturn = stripeCheckoutReturnState(searchParams);
    if (!stripeReturn) {
      return;
    }
    setPortalMessage(t(stripeReturn.messageKey));
    setPortalMessageSeverity(stripeReturn.severity);
    const next = new URLSearchParams(searchParams);
    next.delete('stripe');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const loadBilling = useCallback(() => {
    if (!activeCustomerId) return;

    setLoading(true);
    setError('');

    // Fail-open: do not let a hung Stripe/overview call hide OMEGA invoices (R4/R6 P1).
    const watchdog = setTimeout(() => setLoading(false), 12_000);

    Promise.allSettled([
      getCustomerPortalBilling(activeCustomerId),
      getPortalCustomerBillingOverview(activeCustomerId),
      getPortalCustomerInvoices(activeCustomerId),
      getPortalCustomerBillingProfile(activeCustomerId),
      getPortalCustomerPricingSummary(activeCustomerId),
      getPortalPaidPeriods(activeCustomerId),
      getPortalPaymentMethods(activeCustomerId).catch(() => []),
    ])
      .then(([billingResult, overviewResult, invoicesResult, profileResult, pricingResult, periodsResult, methodsResult]) => {
        const billingData = billingResult.status === 'fulfilled' ? billingResult.value : { subscriptions: [], payments: [] };
        const overviewData = overviewResult.status === 'fulfilled' ? overviewResult.value : null;
        const invoicesData = invoicesResult.status === 'fulfilled' ? invoicesResult.value : null;
        const profileData = profileResult.status === 'fulfilled' ? profileResult.value : {};
        const pricingData = pricingResult.status === 'fulfilled' ? pricingResult.value : null;
        const periodsData = periodsResult.status === 'fulfilled' ? periodsResult.value : [];
        const methodsData = methodsResult.status === 'fulfilled' ? methodsResult.value : [];

        setBilling(billingData);
        setOverview(overviewData);
        setInvoices(invoicesData?.data || []);
        setProfileForm(profileData || {});
        setPricingSummary(pricingData);
        setPaidPeriods(periodsData || []);
        setPaymentMethods(Array.isArray(methodsData) ? methodsData : []);

        const coreFailed = [billingResult, overviewResult, invoicesResult].every((r) => r.status === 'rejected');
        if (coreFailed) {
          setError(t('billing:load_billing_failed'));
        }

        const subscription = (billingData.subscriptions || [])[0] || overviewData?.subscription || null;
        setActiveSubscription(subscription);
        const tenantId = subscription?.tenant_id;
        const cleanupPromise = tenantId
          ? getPortalRolePermissionCleanupSummary(activeCustomerId, tenantId).catch(() => null)
          : Promise.resolve(null);

        if (subscription?.id && activeCustomerId) {
          getPortalSubscriptionChangeRequests(activeCustomerId, subscription.id)
            .then((rows) => setChangeRequests(Array.isArray(rows) ? rows : []))
            .catch(() => setChangeRequests([]));
        } else {
          setChangeRequests([]);
        }

        if (subscription?.status === 'trial' && subscription?.id && activeCustomerId) {
          setTrialConsentAccepted(false);
          getPortalTrialConversion(activeCustomerId, subscription.id)
            .then((row) => setTrialConversion(row))
            .catch(() => setTrialConversion(null));
        } else {
          setTrialConversion(null);
          setTrialConsentAccepted(false);
        }

        cleanupPromise.then(setCleanupSummary);
        getPortalSavedCard(activeCustomerId)
          .then((row) => {
            setSavedCard(row);
            setSavedCardConsent(false);
          })
          .catch(() => setSavedCard(null));
      })
      .finally(() => {
        clearTimeout(watchdog);
        setLoading(false);
      });
  }, [activeCustomerId, t]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const cancelFutureRenewal = useCallback(async () => {
    if (!activeCustomerId || !activeSubscription?.id) return;
    setCancelingRenewal(true);
    setPortalMessage('');
    try {
      const result = await cancelPortalSubscriptionRenewal(activeCustomerId, activeSubscription.id);
      if (result?.meta?.renewal_invoice_review_required) {
        setPortalMessage(t('subscriptionChanges:renewalInvoiceReview'));
        setPortalMessageSeverity('warning');
      } else if (activeSubscription.status === 'trial') {
        setPortalMessage(t('subscriptionChanges:renewalCanceledTrial'));
        setPortalMessageSeverity('success');
      } else {
        setPortalMessage(t('subscriptionChanges:renewalCanceled'));
        setPortalMessageSeverity('success');
      }
      loadBilling();
    } catch {
      setPortalMessage(t('subscriptionChanges:renewalCancelFailed'));
      setPortalMessageSeverity('error');
    } finally {
      setCancelingRenewal(false);
    }
  }, [activeCustomerId, activeSubscription, loadBilling, t]);

  const submitTrialConsent = useCallback(async () => {
    if (!activeCustomerId || !activeSubscription?.id || trialConsentAccepted !== true || !trialConversion?.quote) {
      return;
    }
    setSavingTrialConsent(true);
    setPortalMessage('');
    try {
      const quote = trialConversion.quote;
      const saved = await storePortalTrialConversionConsent(activeCustomerId, activeSubscription.id, {
        accepted: true,
        terms_version: quote.terms_version,
        billing_interval: quote.billing_interval,
        module_codes: quote.module_codes,
        users_count: quote.users_count,
        amount: quote.amount,
      });
      setTrialConversion(saved);
      setTrialConsentAccepted(false);
      setPortalMessage(t('subscriptionChanges:trialConversionSaved'));
      setPortalMessageSeverity('success');
    } catch {
      setPortalMessage(t('subscriptionChanges:trialConversionFailed'));
      setPortalMessageSeverity('error');
    } finally {
      setSavingTrialConsent(false);
    }
  }, [activeCustomerId, activeSubscription, t, trialConsentAccepted, trialConversion]);

  const revokeTrialConsent = useCallback(async () => {
    if (!activeCustomerId || !activeSubscription?.id) return;
    setSavingTrialConsent(true);
    setPortalMessage('');
    try {
      const saved = await revokePortalTrialConversionConsent(activeCustomerId, activeSubscription.id);
      setTrialConversion(saved);
      setTrialConsentAccepted(false);
      setPortalMessage(t('subscriptionChanges:trialConversionRevoked'));
      setPortalMessageSeverity('success');
    } catch {
      setPortalMessage(t('subscriptionChanges:trialConversionFailed'));
      setPortalMessageSeverity('error');
    } finally {
      setSavingTrialConsent(false);
    }
  }, [activeCustomerId, activeSubscription, t]);

  const openStripePortal = useCallback(async () => {
    if (!activeCustomerId) return;
    setPortalMessage('');
    try {
      const session = await createCustomerPortalStripeSession(activeCustomerId);
      if (session.enabled && session.url) {
        window.location.href = session.url;
        return;
      }
      setPortalMessage(session.message || t('billing:stripe_portal_unavailable'));
      setPortalMessageSeverity('warning');
    } catch {
      setPortalMessage(t('billing:stripe_portal_unavailable'));
      setPortalMessageSeverity('warning');
    }
  }, [activeCustomerId, t]);

  const saveProfile = async () => {
    if (!activeCustomerId) return;
    setProfileMessage('');
    setSavingProfile(true);
    try {
      const updated = await updatePortalCustomerBillingProfile(activeCustomerId, profileForm);
      setProfileForm(updated);
      setProfileMessage(t('billing:profile_saved'));
      setProfileMessageSeverity('success');
    } catch {
      setProfileMessage(t('billing:profile_update_failed'));
      setProfileMessageSeverity('error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePayOnline = useCallback(async (invoiceId) => {
    if (!activeCustomerId) return;
    setPortalMessage('');
    try {
      const session = await createPortalStripeCheckoutSession(activeCustomerId, invoiceId);
      if (session.status === 'disabled') {
        setPortalMessage(t('stripe:errors.checkoutDisabled'));
        setPortalMessageSeverity('warning');
        return;
      }
      const url = session.checkout_url || session.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setPortalMessage(session.message || t('billing:stripe_checkout_failed'));
      setPortalMessageSeverity('error');
    } catch (err) {
      if (err?.response?.status === 503) {
        setPortalMessage(t('stripe:errors.checkoutDisabled'));
        setPortalMessageSeverity('warning');
        return;
      }
      setPortalMessage(t('billing:stripe_checkout_failed'));
      setPortalMessageSeverity('error');
    }
  }, [activeCustomerId, t]);

  const handleInvoiceDownload = useCallback(async (invoice) => {
    try {
      if (invoice.pdf_status === 'generated') {
        const blob = await downloadPortalCustomerInvoicePdf(activeCustomerId, invoice.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoice.public_number || `invoice-${invoice.id}`}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        return;
      }
      const info = await downloadPortalCustomerInvoice(activeCustomerId, invoice.id);
      if (info.status === 'external_url' && info.url) {
        window.open(info.url, '_blank', 'noopener,noreferrer');
        return;
      }
      setPortalMessage(info.message || t('billing:invoices.pdf.notAvailable'));
      setPortalMessageSeverity('info');
    } catch {
      setPortalMessage(t('billing:invoices.pdf.notAvailable'));
      setPortalMessageSeverity('warning');
    }
  }, [activeCustomerId, t]);

  const hasPayable = invoices.some((invoice) => !['paid', 'void', 'canceled'].includes(invoice.status))
    || changeRequests.some((change) => change.status === 'pending_payment' && change.invoice_id);

  const customerTypeLabel = profileForm.customer_type === 'business'
    ? t('customers:billingProfile.customerTypeBusiness')
    : profileForm.customer_type === 'consumer'
      ? t('customers:billingProfile.customerTypeConsumer')
      : profileForm.customer_type
        ? t('customers:billingProfile.customerTypeUnknown')
        : emptyValue;

  const paidPeriodColumns = useMemo(() => [
    {
      id: 'coverage',
      label: t('billing:coverage_period'),
      render: (row) => `${row.period_start} → ${row.period_end}`,
    },
    {
      id: 'payment_method',
      label: t('billing:payment_method'),
      render: (row) => row.payment_method || emptyValue,
    },
    {
      id: 'amount',
      label: t('billing:amount'),
      render: (row) => `${row.amount} ${row.currency}`,
    },
  ], [emptyValue, t]);

  const moduleColumns = useMemo(() => [
    {
      id: 'module',
      label: t('pricing:register.line_module'),
      render: (row) => row.name || row.module_code || emptyValue,
    },
    {
      id: 'monthly',
      label: t('pricing:portal.monthly_total'),
      render: (row) => (
        row.monthly_price_snapshot != null
          ? t('pricing:currency_amount', {
            amount: row.monthly_price_snapshot,
            currency: pricingSummary?.currency || 'EUR',
          })
          : emptyValue
      ),
    },
  ], [emptyValue, pricingSummary?.currency, t]);

  const invoiceColumns = useMemo(() => [
    {
      id: 'public_number',
      label: t('billing:number'),
      width: 180,
      render: (row) => (
        <Box>
          <Typography component="span" sx={numberSx}>
            {getInvoiceDisplayNumber(row, emptyValue)}
          </Typography>
          {isExternalAccountingInvoice(row) && (
            <Typography component="div" sx={{ fontSize: 11, color: semantic.text.secondary, mt: 0.25 }}>
              {t('billing:externalAccounting.sourceExternal')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        <StatusBadge
          status={row.display_status || row.status}
          category="invoice"
          size="Sm"
        />
      ),
    },
    {
      id: 'amount',
      label: t('billing:amount'),
      render: (row) => `${row.total_amount} ${row.currency}`,
    },
    {
      id: 'due',
      label: t('billing:due'),
      render: (row) => (
        row.due_at ? new Date(row.due_at).toLocaleDateString() : emptyValue
      ),
    },
    {
      id: 'paid',
      label: t('billing:paid'),
      render: (row) => (
        row.paid_at ? new Date(row.paid_at).toLocaleDateString() : emptyValue
      ),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      render: (row) => {
        if (row.status === 'paid' || row.status === 'void') {
          return emptyValue;
        }

        const showManualWait = ['manual_invoice', 'manual_bank_transfer', 'bank_transfer'].includes(
          row.payment_method,
        );
        const showBankDetails = row.bank_transfer_details_available && row.bank_transfer_details?.iban;

        if (!stripeUi.showPayOnlineCta && !showManualWait && !showBankDetails) {
          return emptyValue;
        }

        return (
          <Box sx={{ minWidth: 0, maxWidth: 220 }}>
            {stripeUi.showPayOnlineCta && (
              <Button
                variant="Secondary"
                onClick={() => handlePayOnline(row.id)}
                sx={{ minHeight: 32, px: 1.25 }}
              >
                {t('stripe:actions.payWithStripe')}
              </Button>
            )}
            {showManualWait && (
              <Typography sx={mutedHelperSx}>
                {t('billing:waiting_manual_confirmation')}
              </Typography>
            )}
            {showBankDetails && (
              <Box sx={{ mt: 0.5 }}>
                <Typography sx={mutedHelperSx}>IBAN: {row.bank_transfer_details.iban}</Typography>
                {row.bank_transfer_details.bic && (
                  <Typography sx={mutedHelperSx}>BIC: {row.bank_transfer_details.bic}</Typography>
                )}
                {row.bank_transfer_details.variable_symbol && (
                  <Typography sx={mutedHelperSx}>
                    {t('billing:invoices.qr.variableSymbol')}: {row.bank_transfer_details.variable_symbol}
                  </Typography>
                )}
                {row.bank_transfer_details.amount && (
                  <Typography sx={mutedHelperSx}>
                    {t('billing:amount')}: {row.bank_transfer_details.amount}{' '}
                    {row.bank_transfer_details.currency || row.currency}
                  </Typography>
                )}
                {row.bank_transfer_details.due_date && (
                  <Typography sx={mutedHelperSx}>
                    {t('billing:due')}: {row.bank_transfer_details.due_date}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      id: 'download',
      label: t('common:download'),
      align: 'right',
      width: 140,
      render: (row) => {
        if (isExternalAccountingInvoice(row)) {
          return (
            <Typography sx={{ fontSize: 12, color: semantic.text.secondary, textAlign: 'right' }}>
              {t('billing:externalAccounting.pdfNotApplicable')}
            </Typography>
          );
        }
        if (canDownloadInvoicePdf(row)) {
          return (
            <Button
              variant="Ghost"
              onClick={() => handleInvoiceDownload(row)}
              sx={{ minHeight: 32, px: 1.25 }}
            >
              {t('billing:invoices.pdf.download')}
            </Button>
          );
        }
        return (
          <Button variant="Ghost" disabled sx={{ minHeight: 32, px: 1.25 }}>
            {t('billing:invoices.pdf.notAvailable')}
          </Button>
        );
      },
    },
  ], [emptyValue, handleInvoiceDownload, handlePayOnline, stripeUi.showPayOnlineCta, t]);

  const subscriptionColumns = useMemo(() => [
    {
      id: 'plan',
      label: t('billing:plan'),
      render: (row) => row.plan?.name || emptyValue,
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        row.status
          ? <StatusBadge status={row.status} category="subscription" size="Sm" />
          : emptyValue
      ),
    },
    {
      id: 'trial_ends',
      label: t('billing:trial_ends'),
      render: (row) => (
        row.trial_ends_at
          ? new Date(row.trial_ends_at).toLocaleDateString()
          : emptyValue
      ),
    },
  ], [emptyValue, t]);

  const paymentColumns = useMemo(() => [
    {
      id: 'amount',
      label: t('billing:amount'),
      render: (row) => `${row.amount} ${row.currency}`,
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        row.status
          ? <StatusBadge status={row.status} category="payment" size="Sm" />
          : emptyValue
      ),
    },
    {
      id: 'due',
      label: t('billing:due'),
      render: (row) => (
        row.due_at ? new Date(row.due_at).toLocaleDateString() : emptyValue
      ),
    },
  ], [emptyValue, t]);

  const changeColumns = useMemo(() => [
    {
      id: 'change_type',
      label: t('subscriptionChanges:changeType'),
      render: (row) => t(`subscriptionChanges:${row.change_type}`),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        <StatusBadge status={row.status} category="invoice" size="Sm" />
      ),
    },
    {
      id: 'effective_at',
      label: t('subscriptionChanges:effectiveAt'),
      render: (row) => (
        row.effective_at
          ? new Date(row.effective_at).toLocaleString()
          : emptyValue
      ),
    },
    {
      id: 'price_delta',
      label: t('subscriptionChanges:priceDelta'),
      render: (row) => `${row.price_delta_amount} ${row.currency}`,
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => {
        if (row.status === 'scheduled') {
          return (
            <Button
              variant="Ghost"
              onClick={() => cancelPortalSubscriptionChange(activeCustomerId, row.id).then(loadBilling)}
              sx={{ minHeight: 32, px: 1.25 }}
            >
              {t('subscriptionChanges:cancelScheduledDowngrade')}
            </Button>
          );
        }
        if (row.status === 'pending_payment' && row.invoice_id) {
          if (!stripeUi.showPayOnlineCta) {
            return (
              <Typography sx={mutedHelperSx}>
                {t('billing:waiting_manual_confirmation')}
              </Typography>
            );
          }
          return (
            <Button
              variant="Secondary"
              onClick={() => handlePayOnline(row.invoice_id)}
              sx={{ minHeight: 32, px: 1.25 }}
            >
              {t('stripe:actions.payWithStripe')}
            </Button>
          );
        }
        return emptyValue;
      },
    },
  ], [activeCustomerId, emptyValue, handlePayOnline, loadBilling, stripeUi.showPayOnlineCta, t]);

  if (portalLoading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const paymentStatusValue = overview?.payment_flow_status || overview?.payment_status || emptyValue;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('billing:billing_title')}
        subtitle={t('billing:billing_subtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {stripeUi.showStripePortalCta && (
              <Button variant="Secondary" onClick={openStripePortal}>
                {t('billing:open_stripe_portal')}
              </Button>
            )}
            {activeSubscription && (
              <Button variant="Accent" onClick={() => setChangeDialogOpen(true)}>
                {t('subscriptionChanges:changePackage')}
              </Button>
            )}
            {activeSubscription
              && !activeSubscription.cancel_at_period_end
              && ['trial', 'active', 'past_due'].includes(activeSubscription.status) && (
              <Button
                variant="Ghost"
                disabled={cancelingRenewal}
                onClick={cancelFutureRenewal}
              >
                {t('subscriptionChanges:cancelFutureRenewal')}
              </Button>
            )}
          </Stack>
        )}
      />

      {cleanupSummary?.pending_cleanup_exists && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('permissionCleanup:customerPortalSummary')}
        </Alert>
      )}
      {stripeUi.showSavedCardSection && savedCard && (
        <SavedCardSection
          summary={savedCard}
          consentAccepted={savedCardConsent}
          onConsentChange={setSavedCardConsent}
          busy={savingCard}
          onAdd={async () => {
            if (!activeCustomerId || savedCardConsent !== true) return;
            setSavingCard(true);
            try {
              const result = await createPortalSavedCardSetup(activeCustomerId, {
                accepted: true,
                consent_version: savedCard.consent_version,
                subscription_id: activeSubscription?.id ?? null,
              });
              if (result?.url) {
                window.location.assign(result.url);
                return;
              }
              setPortalMessage(t('stripe:savedCard.unavailable'));
              setPortalMessageSeverity('info');
            } catch {
              setPortalMessage(t('stripe:savedCard.unavailable'));
              setPortalMessageSeverity('error');
            } finally {
              setSavingCard(false);
            }
          }}
          onRevoke={async () => {
            if (!activeCustomerId || !savedCard?.method?.id) return;
            setSavingCard(true);
            try {
              await revokePortalSavedCard(activeCustomerId, savedCard.method.id);
              setSavedCardConsent(false);
              loadBilling();
            } catch {
              setPortalMessage(t('stripe:savedCard.unavailable'));
              setPortalMessageSeverity('error');
            } finally {
              setSavingCard(false);
            }
          }}
          onRemove={async () => {
            if (!activeCustomerId || !savedCard?.method?.id) return;
            setSavingCard(true);
            try {
              await detachPortalSavedCard(activeCustomerId, savedCard.method.id);
              setSavedCardConsent(false);
              loadBilling();
            } catch {
              setPortalMessage(t('stripe:savedCard.unavailable'));
              setPortalMessageSeverity('error');
            } finally {
              setSavingCard(false);
            }
          }}
        />
      )}
      {activeSubscription?.status === 'trial' && trialConversion && (
        <TrialConversionConsentPanel
          eligibility={trialConversion}
          accepted={trialConsentAccepted}
          onAcceptedChange={setTrialConsentAccepted}
          onSubmit={submitTrialConsent}
          onRevoke={revokeTrialConsent}
          submitting={savingTrialConsent}
        />
      )}
      {portalMessage && (
        <Alert severity={portalMessageSeverity} sx={{ mb: 2 }}>
          {portalMessage}
        </Alert>
      )}
      {!loading && !hasPayable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {stripeUi.showPayOnlineCta
            ? t('stripe:messages.noOpenPayment')
            : t('billing:no_invoices_yet')}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadBilling} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <LoadingSkeleton type="CardGrid" rows={5} sx={{ mb: 2.5 }} />
          <LoadingSkeleton type="Table" embedded={false} rows={4} columns={5} />
        </>
      ) : (
        <>
          {overview && (
            <Box sx={metricGridSx}>
              <MetricCard
                label={t('billing:current_plan')}
                value={overview.current_plan?.name || emptyValue}
              />
              <MetricCard
                label={t('billing:amount_due')}
                value={`${overview.amount_due} EUR`}
              />
              <MetricCard
                label={t('billing:payment_status')}
                value={paymentStatusValue}
              />
              <MetricCard
                label={t('billing:paid_until')}
                value={overview.paid_until || emptyValue}
              />
              <MetricCard
                label={t('billing:grace_until')}
                value={overview.grace_until || emptyValue}
              />
            </Box>
          )}

          {paidPeriods.length > 0 && (
            <SectionCard variant="Default" noPadding sx={{ mb: 2.5, width: '100%' }}>
              <TableToolbar
                mode="Default"
                title={t('billing:paid_periods_title')}
                meta={(
                  <CountBadge
                    label={t('billing:portal.paid_periods_toolbar_count', { count: paidPeriods.length })}
                  />
                )}
              />
              <DataTable
                type="Default"
                embedded
                columns={paidPeriodColumns}
                rows={paidPeriods}
                getRowKey={(row) => row.id}
                emptyMessage={t('billing:no_paid_periods')}
                sx={tableSx}
              />
            </SectionCard>
          )}

          {pricingSummary && (
            <SectionCard
              variant="Default"
              title={t('pricing:portal.pricing_summary_title')}
              subtitle={t('pricing:portal.pricing_summary_subtitle')}
              sx={{ mb: 2.5 }}
            >
              <Box sx={{ ...detailGridSx, mb: 2.5 }}>
                <DetailField
                  label={t('pricing:portal.users_count')}
                  value={pricingSummary.users_count ?? emptyValue}
                />
                <DetailField
                  label={t('pricing:portal.licensed_user_limit')}
                  value={pricingSummary.max_users ?? emptyValue}
                />
                <DetailField
                  label={t('pricing:portal.user_tier')}
                  value={pricingSummary.user_tier_label || emptyValue}
                />
                <DetailField
                  label={t('pricing:portal.billing_interval')}
                  value={
                    pricingSummary.billing_interval
                      ? formatBillingInterval(pricingSummary.billing_interval)
                      : emptyValue
                  }
                />
                <DetailField
                  label={t('pricing:portal.monthly_total')}
                  value={
                    pricingSummary.monthly_total != null
                      ? t('pricing:currency_amount', {
                        amount: pricingSummary.monthly_total,
                        currency: pricingSummary.currency || 'EUR',
                      })
                      : emptyValue
                  }
                />
                <DetailField
                  label={t('pricing:portal.yearly_total')}
                  value={
                    pricingSummary.yearly_total != null
                      ? t('pricing:currency_amount', {
                        amount: pricingSummary.yearly_total,
                        currency: pricingSummary.currency || 'EUR',
                      })
                      : emptyValue
                  }
                />
                <DetailField
                  label={t('pricing:portal.annual_discount')}
                  value={
                    pricingSummary.annual_discount_amount != null
                      ? t('pricing:currency_amount', {
                        amount: pricingSummary.annual_discount_amount,
                        currency: pricingSummary.currency || 'EUR',
                      })
                      : emptyValue
                  }
                />
                <DetailField
                  label={t('pricing:portal.effective_amount')}
                  value={
                    pricingSummary.effective_amount != null
                      ? t('pricing:currency_amount', {
                        amount: pricingSummary.effective_amount,
                        currency: pricingSummary.currency || 'EUR',
                      })
                      : emptyValue
                  }
                />
                {pricingSummary.commercial?.agreement_applied && (
                  <>
                    <DetailField
                      label={t('pricing:portal.commercial_discount')}
                      value={t('pricing:currency_amount', {
                        amount: pricingSummary.commercial.commercial_discount_amount ?? 0,
                        currency: pricingSummary.currency || 'EUR',
                      })}
                    />
                    <DetailField
                      label={t('pricing:portal.commercial_label')}
                      value={pricingSummary.commercial.agreement_label || emptyValue}
                    />
                    <DetailField
                      label={t('pricing:portal.commercial_validity')}
                      value={
                        pricingSummary.commercial.valid_until
                        || (pricingSummary.commercial.periods_remaining != null
                          ? t('pricing:portal.periods_remaining', {
                            count: pricingSummary.commercial.periods_remaining,
                          })
                          : emptyValue)
                      }
                    />
                  </>
                )}
              </Box>

              {(pricingSummary.modules || []).length > 0 ? (
                <Box sx={{ mt: 1, borderTop: `1px solid ${semantic.border.subtle}`, mx: -2, mb: -2 }}>
                  <DataTable
                    type="Default"
                    embedded
                    columns={moduleColumns}
                    rows={pricingSummary.modules}
                    getRowKey={(row) => row.module_code}
                    emptyMessage={t('pricing:portal.no_modules')}
                    sx={tableSx}
                  />
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: '18px',
                    color: semantic.text.muted,
                  }}
                >
                  {t('pricing:portal.no_modules')}
                </Typography>
              )}
            </SectionCard>
          )}

          <SectionCard variant="Default" noPadding sx={{ mb: 2.5, width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('billing:invoices.title')}
              meta={(
                <CountBadge
                  label={t('billing:portal.invoices_toolbar_count', { count: invoices.length })}
                />
              )}
            />
            <DataTable
              type="Default"
              embedded
              columns={invoiceColumns}
              rows={invoices}
              getRowKey={(row) => row.id}
              emptyMessage={t('billing:no_invoices_yet')}
              sx={tableSx}
            />
          </SectionCard>

          <SectionCard
            variant="Default"
            title={t('customers:billingProfile.title')}
            sx={{ mb: 2.5 }}
          >
            {profileMessage && (
              <Alert severity={profileMessageSeverity} sx={{ mb: 2 }}>
                {profileMessage}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2.5 }}>
              {t('customers:billingProfile.managedByAdmin')}
            </Alert>

            <Box sx={detailGridSx}>
              <TextInput
                density="Normal"
                label={t('customers:billingProfile.billingEmail')}
                value={profileForm.billing_email || ''}
                onChange={(e) => setProfileForm({ ...profileForm, billing_email: e.target.value })}
              />
              <TextInput
                density="Normal"
                label={t('billing:contact_name')}
                value={profileForm.billing_contact_name || ''}
                onChange={(e) => setProfileForm({ ...profileForm, billing_contact_name: e.target.value })}
              />
              <TextInput
                density="Normal"
                label={t('customers:billingProfile.billingName')}
                value={profileForm.company_name || ''}
                onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
              />
              <TextInput
                density="Normal"
                label={t('customers:billingProfile.countryCode')}
                value={profileForm.country || ''}
                onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
              />
              <TextInput
                density="Normal"
                label={t('customers:billingProfile.customerType')}
                value={customerTypeLabel}
                readOnly
              />
              <TextInput
                density="Normal"
                label={t('customers:billingProfile.taxRulePreview')}
                value={profileForm.tax_note || profileForm.tax_rule || emptyValue}
                readOnly
              />
              {profileForm.customer_type === 'business' && (
                <>
                  <TextInput
                    density="Normal"
                    label={t('customers:billingProfile.companyId')}
                    value={profileForm.company_id_number || profileForm.company_id || ''}
                    readOnly
                  />
                  <TextInput
                    density="Normal"
                    label={t('customers:billingProfile.vatId')}
                    value={profileForm.vat_id || ''}
                    readOnly
                  />
                  <TextInput
                    density="Normal"
                    label={t('customers:billingProfile.taxId')}
                    value={profileForm.tax_id || ''}
                    readOnly
                  />
                </>
              )}
            </Box>

            {profileForm.customer_type === 'consumer' && (
              <Alert severity="info" sx={{ mt: 2.5 }}>
                {t('customers:billingProfile.consumerNoCompanyIds')}
              </Alert>
            )}

            <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="Accent"
                loading={savingProfile}
                disabled={savingProfile}
                onClick={saveProfile}
              >
                {t('billing:save_profile')}
              </Button>
            </Box>
          </SectionCard>

          <SectionCard variant="Default" noPadding sx={{ mb: 2.5, width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('billing:subscriptions')}
              meta={(
                <CountBadge
                  label={t('billing:portal.subscriptions_toolbar_count', {
                    count: (billing.subscriptions || []).length,
                  })}
                />
              )}
            />
            <DataTable
              type="Default"
              embedded
              columns={subscriptionColumns}
              rows={billing.subscriptions || []}
              getRowKey={(row) => row.id}
              emptyMessage={t('billing:no_subscriptions')}
              sx={tableSx}
            />
          </SectionCard>

          <SectionCard variant="Default" noPadding sx={{ mb: 2.5, width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('billing:payments_title')}
              meta={(
                <CountBadge
                  label={t('billing:portal.payments_toolbar_count', {
                    count: (billing.payments || []).length,
                  })}
                />
              )}
            />
            <DataTable
              type="Default"
              embedded
              columns={paymentColumns}
              rows={billing.payments || []}
              getRowKey={(row) => row.id}
              emptyMessage={t('billing:no_payments')}
              sx={tableSx}
            />
          </SectionCard>

          <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('subscriptionChanges:pendingChanges')}
              meta={(
                <CountBadge
                  label={t('billing:portal.changes_toolbar_count', {
                    count: changeRequests.length,
                  })}
                />
              )}
            />
            <Box sx={{ px: 2.5, pb: 1.5 }}>
              <Alert severity="info">
                {t('subscriptionChanges:noRefundPolicy')}
              </Alert>
            </Box>
            <DataTable
              type="Default"
              embedded
              columns={changeColumns}
              rows={changeRequests}
              getRowKey={(row) => row.id}
              emptyMessage={t('subscriptionChanges:noChanges')}
              sx={tableSx}
            />
          </SectionCard>
        </>
      )}

      <SubscriptionChangeDialog
        open={changeDialogOpen}
        onClose={() => setChangeDialogOpen(false)}
        mode="portal"
        customerId={activeCustomerId}
        subscription={activeSubscription}
        initialModules={(pricingSummary?.modules || []).map((m) => m.module_code)}
        initialUsersCount={pricingSummary?.users_count || activeSubscription?.users_count || 10}
        initialBillingInterval={pricingSummary?.billing_interval || activeSubscription?.billing_interval || 'monthly'}
        onSuccess={loadBilling}
      />
    </Box>
  );
}
